<script setup lang="ts">
import { message } from 'ant-design-vue'
import { ZoomIn, ZoomOut } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { useI18n } from '../composables/useI18n'
import en from './locales/en'
import zh from './locales/zh'

const { t } = useI18n({ zh, en })

const props = defineProps<{ open: boolean; src: string }>()
const emit = defineEmits<{ 'update:open': [value: boolean]; confirm: [file: File]; cancel: [] }>()

// Square crop viewport size in px; small enough to fit a 90vw modal on phones.
const CROP = 240
// Output avatar edge length in px.
const OUTPUT = 512

const imgEl = ref<HTMLImageElement | null>(null)
const imgReady = ref(false)
const processing = ref(false)
const naturalW = ref(0)
const naturalH = ref(0)
const zoom = ref(1)
// Source image coordinates aligned to the viewport center.
const centerX = ref(0)
const centerY = ref(0)

// Base scale that makes the image exactly cover the crop viewport at zoom = 1.
const cover = computed(() =>
  naturalW.value && naturalH.value ? Math.max(CROP / naturalW.value, CROP / naturalH.value) : 1,
)
const eff = computed(() => cover.value * zoom.value)
const dispW = computed(() => naturalW.value * eff.value)
const dispH = computed(() => naturalH.value * eff.value)

const imgStyle = computed(() => ({
  width: `${dispW.value}px`,
  height: `${dispH.value}px`,
  transform: `translate(${CROP / 2 - centerX.value * eff.value}px, ${CROP / 2 - centerY.value * eff.value}px)`,
}))

const zoomPercent = computed({
  get: () => Math.round(zoom.value * 100),
  set: (value: number) => {
    zoom.value = value / 100
    clampCenter()
  },
})

function clampCenter() {
  const halfW = CROP / (2 * eff.value)
  const halfH = CROP / (2 * eff.value)
  centerX.value = Math.min(Math.max(centerX.value, halfW), naturalW.value - halfW)
  centerY.value = Math.min(Math.max(centerY.value, halfH), naturalH.value - halfH)
}

function reset() {
  imgReady.value = false
  processing.value = false
  naturalW.value = 0
  naturalH.value = 0
  zoom.value = 1
  centerX.value = 0
  centerY.value = 0
}

watch(
  () => props.src,
  () => reset(),
)

function onLoad() {
  const el = imgEl.value
  if (!el) return
  naturalW.value = el.naturalWidth
  naturalH.value = el.naturalHeight
  centerX.value = naturalW.value / 2
  centerY.value = naturalH.value / 2
  zoom.value = 1
  clampCenter()
  imgReady.value = true
}

let dragging = false
let lastX = 0
let lastY = 0

function onPointerDown(e: PointerEvent) {
  if (!imgReady.value) return
  dragging = true
  lastX = e.clientX
  lastY = e.clientY
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
}

function onPointerMove(e: PointerEvent) {
  if (!dragging) return
  centerX.value -= (e.clientX - lastX) / eff.value
  centerY.value -= (e.clientY - lastY) / eff.value
  lastX = e.clientX
  lastY = e.clientY
  clampCenter()
}

function onPointerUp() {
  dragging = false
}

function onWheel(e: WheelEvent) {
  if (!imgReady.value) return
  zoom.value = Math.min(3, Math.max(1, zoom.value - e.deltaY * 0.001))
  clampCenter()
}

async function confirm() {
  const el = imgEl.value
  if (!el || !imgReady.value) return
  processing.value = true
  try {
    const canvas = document.createElement('canvas')
    canvas.width = OUTPUT
    canvas.height = OUTPUT
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas context unavailable')
    const region = CROP / eff.value
    const sx = Math.min(Math.max(centerX.value - region / 2, 0), naturalW.value - region)
    const sy = Math.min(Math.max(centerY.value - region / 2, 0), naturalH.value - region)
    ctx.imageSmoothingQuality = 'high'
    // JPEG has no alpha channel: fill white first, otherwise transparent areas turn black
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, OUTPUT, OUTPUT)
    ctx.drawImage(el, sx, sy, region, region, 0, 0, OUTPUT, OUTPUT)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92))
    if (!blob) throw new Error('toBlob failed')
    emit('confirm', new File([blob], 'avatar.jpg', { type: 'image/jpeg' }))
    emit('update:open', false)
  } catch (e: any) {
    message.error(e?.message || t('base.avatarCrop.processFailed'))
  } finally {
    processing.value = false
  }
}

function cancel() {
  emit('cancel')
  emit('update:open', false)
}
</script>

<template>
  <a-modal
    :open="open"
    width="min(600px, 90vw)"
    :title="t('base.avatarCrop.title')"
    :mask-closable="false"
    :ok-text="t('common.confirm')"
    :cancel-text="t('common.cancel')"
    :confirm-loading="processing"
    :ok-button-props="{ disabled: !imgReady }"
    @ok="confirm"
    @cancel="cancel"
  >
    <div class="flex flex-col items-center gap-4 py-2">
      <div
        class="relative overflow-hidden rounded-full bg-black/60 touch-none cursor-grab active:cursor-grabbing select-none"
        :style="{ width: `${CROP}px`, height: `${CROP}px` }"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @wheel.prevent="onWheel"
      >
        <img
          v-show="imgReady"
          ref="imgEl"
          :src="src"
          alt=""
          class="absolute left-0 top-0 max-w-none pointer-events-none select-none"
          :style="imgStyle"
          draggable="false"
          @load="onLoad"
        />
        <div v-if="!imgReady" class="absolute inset-0 flex items-center justify-center">
          <a-spin />
        </div>
      </div>

      <div class="flex items-center gap-3" :style="{ width: `${CROP}px` }">
        <ZoomOut class="w-4 h-4 text-gray-400" aria-hidden="true" />
        <a-slider v-model:value="zoomPercent" class="flex-1" :min="100" :max="300" :disabled="!imgReady" />
        <ZoomIn class="w-4 h-4 text-gray-400" aria-hidden="true" />
      </div>

      <div class="text-xs text-gray-400 dark:text-gray-500">{{ t('base.avatarCrop.hint') }}</div>
    </div>
  </a-modal>
</template>
